/** DEVIZUL FAȚADEI, sub rândul «Anvelopă» din rezumat: articolele pe capitole
 *  (termosistem, fațadă ventilată, finisaje, glafuri, balustrade, tâmplărie,
 *  terase), fiecare desfăcut pe corpuri, apoi procentele de după costul direct,
 *  cantitățile informative și rândurile de metraj fără tarif.
 *
 *  Numele vin din cheile `bc.fat.*`: serverul dă CODURI (capitol, element,
 *  articol), ca devizul să se citească în limba ecranului. Un cod fără cheie se
 *  citește cu spații în loc de `_`.
 *
 *  CORECȚIILE: dublu clic pe cantitate sau pe un preț unitar deschide câmpul
 *  (Enter salvează, Esc renunță); o valoare schimbată se vede colorată, cu
 *  cea calculată la hover și ↺ care o întoarce. Prețurile sunt ale proiectului,
 *  cantitatea a soluției. Componentele (adeziv, dibluri, plasă…) stau sub
 *  articolul lor și îi urmează cantitatea. */
import { Fragment, useMemo, useRef, useState, useEffect } from "react";
import Termeni, { Lamurire } from "./Termeni";
import type { ArticolFatada, CampCorectie, DevizFatada as Deviz, RandFatada } from "../../api";
import { esteCheie, numar, t, tn, useT } from "../../i18n";
import { bani, pretUnitar } from "../../structure/bani";

const nume = (prefix: string, cod: string) => {
  const k = `${prefix}.${cod}`;
  return esteCheie(k) ? t(k) : cod.replace(/_/g, " ");
};
const UM: Record<string, string> = { m2: "m²", ml: "ml", buc: "buc", kg: "kg" };
const um = (u: string) => (u === "buc" ? t("bc.um.buc") : UM[u] ?? u);
const nr = (v: number, z = 2) => numar(v, { maximumFractionDigits: z });
/** Placa de polistiren de 1000 × 500 mm, pe care se socotesc diblurile. */
const PLACA_EPS_M2 = 0.5;

/** Ce schimbă prețul în interiorul articolului: grosimea, înălțimea,
 *  vitrajul și deschiderea. */
function varianta(a: RandFatada): string {
  return [
    a.grosime_m != null && `${nr(a.grosime_m * 100, 0)} cm`,
    a.inaltime_m != null && `h ${nr(a.inaltime_m)} m`,
    a.vitraj && nume("bc.fat.vitraj", a.vitraj),
    a.deschidere && nume("bc.fat.deschidere", a.deschidere),
  ].filter(Boolean).join(" · ");
}

function Denumire({ a }: { a: RandFatada }) {
  const v = varianta(a);
  if (a.din) {
    // o componentă: sub articolul ei, cu consumul pe unitatea lui
    return (
      <span className="fat-comp">
        {nume("bc.fat.art", a.articol)}
        {a.consum != null && <span className="sg-muted"> · {nr(a.consum, 2)} {um(a.um)}/{um(a.din.split("|")[3] ?? "")}</span>}
        {/* diblurile pe placă: plăcile de polistiren au 1000 × 500 mm */}
        {a.articol.startsWith("dibluri_eps") && a.consum != null && (
          <span className="sg-muted"> · {t("bc.fat.dibluri_pe_placa", { n: nr(a.consum * PLACA_EPS_M2, 1) })}</span>
        )}
      </span>
    );
  }
  return (
    <>
      <span className="sg-muted">{nume("bc.fat.el", a.element)} · </span>
      {nume("bc.fat.art", a.articol)}
      {v && <span className="sg-muted"> · {v}</span>}
    </>
  );
}

/** Un număr scris de om: «1.234,5», «1234.5», «12,5». */
export function citesteNumar(s: string): number | null {
  const x = s.trim().replace(/\s/g, "").replace(/€/g, "");
  if (!x) return null;
  const n = Number(x.includes(",") ? x.replace(/\./g, "").replace(",", ".") : x);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

type LaCorectie = (cheie: string, camp: CampCorectie, valoare: number | null) => Promise<void>;

/** O CELULĂ CORECTABILĂ: dublu clic deschide câmpul; o valoare schimbată de
 *  om e colorată, cu cea calculată la hover și ↺ lângă. */
function Celula({ a, camp, text, laCorectie }: {
  a: ArticolFatada; camp: CampCorectie; text: string; laCorectie?: LaCorectie;
}) {
  const [scrie, setScrie] = useState<string | null>(null);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (scrie !== null) ref.current?.select(); }, [scrie !== null]);
  const mod = a.modificat?.includes(camp);
  const calc = a.calculat?.[camp];
  const trimite = async (v: number | null) => {
    if (!laCorectie) return;
    setLucreaza(true);
    setEroare(null);
    try {
      await laCorectie(a.cheie, camp, v);
      setScrie(null);
    } catch (e) {
      setEroare((e as Error).message || t("bc.fat.nu_s_a_salvat"));
    } finally {
      setLucreaza(false);
    }
  };
  if (scrie !== null) {
    const n = citesteNumar(scrie);
    return (
      <td className="sg-mono fat-edit" onClick={(e) => e.stopPropagation()}>
        <input ref={ref} value={scrie} disabled={lucreaza} aria-invalid={n === null || !!eroare}
               title={eroare ?? undefined}
               aria-label={t(`bc.fat.camp.${camp}`)}
               onChange={(e) => setScrie(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === "Escape") setScrie(null);
                 if (e.key === "Enter" && n !== null) void trimite(n);
               }}
               onBlur={() => { if (!lucreaza) setScrie(null); }} />
        {camp !== "cantitate" && <span className="sg-muted"> €</span>}
      </td>
    );
  }
  return (
    <td className={`sg-mono${laCorectie ? " fat-corectabil" : ""}${mod ? " fat-mod" : ""}`}
        title={mod && calc != null ? t("bc.fat.calculat", { v: nr(calc) }) : laCorectie ? t("bc.fat.dublu_clic") : undefined}
        onDoubleClick={laCorectie ? (e) => { e.stopPropagation(); setScrie(String(a[camp]).replace(".", ",")); } : undefined}>
      {text}
      {mod && laCorectie && (
        <button type="button" className="fat-reset" title={t("bc.fat.revino")} aria-label={t("bc.fat.revino")}
                disabled={lucreaza}
                onClick={(e) => { e.stopPropagation(); void trimite(null); }}>↺</button>
      )}
    </td>
  );
}

export default function DevizFatada({ deviz, laCorectie }: {
  deviz: Deviz;
  /** salvează o corecție și reîncarcă devizul; fără ea, tabelul doar se citește */
  laCorectie?: LaCorectie;
}) {
  useT();
  const [deschis, setDeschis] = useState<number | null>(null);
  const [corp, setCorp] = useState<string | null>(null);
  const corpuri = Object.entries(deviz.corpuri);

  // pe un corp: cantitatea și sumele lui, la aceleași prețuri unitare
  const vazute = useMemo<ArticolFatada[]>(() => {
    if (!corp) return deviz.articole;
    return deviz.articole.filter((a) => a.corpuri[corp]).map((a) => {
      const q = a.corpuri[corp];
      return { ...a, cantitate: q, total_material_eur: q * a.pu_material_eur,
               total_manopera_eur: q * a.pu_manopera_eur };
    });
  }, [deviz, corp]);
  const capitole = useMemo(() => {
    const m = new Map<string, ArticolFatada[]>();
    for (const a of vazute) m.set(a.capitol_cod, [...(m.get(a.capitol_cod) ?? []), a]);
    return [...m.entries()];
  }, [vazute]);
  const totalMat = vazute.reduce((s, a) => s + a.total_material_eur, 0);
  const totalMan = vazute.reduce((s, a) => s + a.total_manopera_eur, 0);
  // pe un corp nu se corectează: cantitatea lui e o parte din a rândului
  const corecteaza = corp ? undefined : laCorectie;
  const modificate = deviz.articole.filter((a) => a.modificat?.length).length;

  return (
    <>
      <div className="bc-art-sus">
        <span className="bc-art-titlu">{t("bc.fat.titlu")}</span>
        {laCorectie && <Lamurire text={t("bc.fat.cum_corectezi")} />}
        <span className="sg-muted">{t("bc.fat.sursa", { versiune: deviz.versiune_tarife })}</span>
        {laCorectie && modificate > 0 && <span className="fat-mod-text">{tn("bc.fat.modificate#n", modificate)}</span>}
      </div>
      {deviz.nepretuite.length > 0 && (
        <div className="rc-lipsa">
          {tn("bc.fat.nepretuite#n", deviz.nepretuite.length, {
            lista: deviz.nepretuite.map((a) => `${nume("bc.fat.art", a.articol)} `
              + `(${nr(a.cantitate)} ${um(a.um)})`).join("; ") })}
        </div>
      )}
      {corpuri.length > 1 && (
        <div className="rc-chips" style={{ margin: "8px 0" }} role="group"
             aria-label={t("bc.fat.corpuri")}>
          <button type="button" className={`rc-chip ${corp == null ? "active" : ""}`}
                  onClick={() => { setCorp(null); setDeschis(null); }}>
            {tn("bc.fat.toate_corpurile#n", corpuri.length)}
          </button>
          {corpuri.map(([c, v]) => (
            <button key={c} type="button" className={`rc-chip ${corp === c ? "active" : ""}`}
                    onClick={() => { setCorp(corp === c ? null : c); setDeschis(null); }}>
              {c} · {bani(v.total_direct_eur)}
            </button>
          ))}
        </div>
      )}
      <table className="sg-table rc-deviz">
        <thead>
          <tr>
            <th>{t("struct.ret.th_nr")}</th>
            <th>{t("struct.ret.th_articol")}</th>
            <th><Termeni text={t("struct.ret.th_um")} /></th>
            <th>{t("struct.ret.th_cantitate")}</th>
            <th><Termeni text={t("struct.ret.th_pu_material")} /></th>
            <th><Termeni text={t("struct.ret.p_u_manopera")} /></th>
            <th>{t("struct.ret.th_total_material")}</th>
            <th>{t("struct.ret.total_manopera")}</th>
          </tr>
        </thead>
        <tbody>
          {capitole.map(([cap, art]) => (
            <Fragment key={cap}>
              <tr className="rc-capitol">
                <td colSpan={8}>
                  <strong>{nume("bc.fat.cap", cap).toUpperCase()}</strong>
                  <span className="sg-mono sg-muted">
                    {" · "}{bani(art.reduce((s, a) => s + a.total_material_eur + a.total_manopera_eur, 0))}
                  </span>
                </td>
              </tr>
              {art.map((a) => (
                <Fragment key={a.nr}>
                  <tr onClick={() => setDeschis(deschis === a.nr ? null : a.nr)}
                      className={a.din ? "fat-rand-comp" : undefined}
                      style={{ cursor: "pointer" }}>
                    <td className="sg-mono">{a.nr}</td>
                    <td>{a.din ? "↳ " : deschis === a.nr ? "▾ " : "▸ "}<Denumire a={a} /></td>
                    <td className="sg-mono">{um(a.um)}</td>
                    <Celula a={a} camp="cantitate" text={nr(a.cantitate)} laCorectie={corecteaza} />
                    <Celula a={a} camp="pu_material_eur" laCorectie={corecteaza}
                            text={a.pu_material_eur ? pretUnitar(a.pu_material_eur) : "—"} />
                    <Celula a={a} camp="pu_manopera_eur" laCorectie={corecteaza}
                            text={a.pu_manopera_eur ? pretUnitar(a.pu_manopera_eur) : "—"} />
                    <td className="sg-mono">{bani(a.total_material_eur)}</td>
                    <td className="sg-mono">{bani(a.total_manopera_eur)}</td>
                  </tr>
                  {deschis === a.nr && (
                    <tr>
                      <td colSpan={8} className="rc-detaliu">
                        <div className="sg-muted" style={{ marginBottom: 4 }}>
                          {t("bc.fat.pret_din", { sursa: a.sursa_pret })}
                        </div>
                        {Object.keys(a.corpuri).length > 1 && (
                          <table className="sg-table">
                            <tbody>
                              {Object.entries(a.corpuri).map(([c, q]) => (
                                <tr key={c}>
                                  <td>{c}</td>
                                  <td className="sg-mono">{nr(q)} {um(a.um)}</td>
                                  <td className="sg-mono">
                                    {bani(q * (a.pu_material_eur + a.pu_manopera_eur))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}><strong>{t("struct.ret.total_cost_direct")}</strong></td>
            <td className="sg-mono"><strong>{bani(totalMat)}</strong></td>
            <td className="sg-mono"><strong>{bani(totalMan)}</strong></td>
          </tr>
        </tfoot>
      </table>

      {!corp && deviz.indirecte.length > 0 && (
        <>
          <div className="rc-sect">{t("struct.ret.cost_direct_pret")}</div>
          <table className="sg-table">
            <tbody>
              <tr>
                <td>{t("struct.ret.cost_direct")}</td>
                <td className="sg-mono">—</td>
                <td className="sg-mono">{bani(deviz.total_direct_eur)}</td>
              </tr>
              {deviz.indirecte.map((i) => (
                <tr key={i.cheie}>
                  <td>{nume("struct.ret.ind", i.cheie)}</td>
                  <td className="sg-mono">{nr(i.procent, 1)} %</td>
                  <td className="sg-mono">{bani(i.valoare_eur)}</td>
                </tr>
              ))}
              <tr>
                <td><strong>{t("struct.ret.total")}</strong></td>
                <td />
                <td className="sg-mono"><strong>{bani(deviz.total_eur)}</strong></td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {!corp && deviz.informative.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="rc-sect" style={{ cursor: "pointer" }}>
            {t("bc.fat.informative")}
            <Lamurire text={t("bc.fat.informative_text")} />
          </summary>
          <table className="sg-table">
            <tbody>
              {deviz.informative.map((a, i) => (
                <tr key={i}>
                  <td><Denumire a={a} /></td>
                  <td className="sg-mono">{nr(a.cantitate)} {um(a.um)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}
